"use client";

import { useCallback } from "react";
import { MdCheck, MdClose } from "react-icons/md";

type Props = {
  title?: string;
  content?: string | React.ReactNode;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  isOpen?: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCancel?: () => void;
}

const ConfirmModal = ({
  title = "Confirm action",
  content = "Are you sure you want to perform this action?",
  cancelButtonLabel = "Cancel",
  confirmButtonLabel = "Confirm",
  isOpen = false,
  isLoading = false,
  onClose,
  onConfirm,
  onCancel,
}: Props) => {
  const dialogRef = useCallback((node: HTMLDialogElement | null) => {
    if (node !== null) {
      if (isOpen) {
        node.showModal();
        // Add event listener for 'Escape' key
        const handleEscape = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            onClose();
          }
        };
        node.addEventListener('keydown', handleEscape);
        // Clean up the event listener when the component unmounts or updates
        return () => {
          node.removeEventListener('keydown', handleEscape);
        };
      } else {
        node.close();
      }
    }
  }, [isOpen, onClose]);

  return (
    <dialog className="modal" ref={dialogRef}>
      <div className="modal-box">
        <h3 className="font-bold text-lg">{title}</h3>
        {typeof content === "string"
          ? <p className="py-4">{content}</p>
          : <div className="py-4">{content}</div>
        }
        <div className="modal-action">
          <form method="dialog">
            <div className="flex flex-row gap-x-2">
              {/* if there is a button in form, it will close the modal */}
              <button
                className="btn btn-outline btn-error"
                type="button"
                onClick={() => {
                  if (onCancel) {
                    onCancel();
                  } else {
                    onClose();
                  }
                }}
              >
                <MdClose />
                {cancelButtonLabel}
              </button>
              <button
                className="btn btn-primary"
                type="button"
                disabled={isLoading}
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
              >
                {isLoading ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  <MdCheck />
                )}
                {confirmButtonLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </dialog>
  );
}

export default ConfirmModal;